'use client';

import { createContext, FC, useContext, useMemo } from 'react';
import { IS_DEVTOOLS_ENABLED } from '@/constants';
import { useMfeVersions } from './hooks/useMfeVersions';
import { useDevToolsPreferences } from './hooks/useDevToolsPreferences';
import type { DevToolsPosition } from './utils';

interface DevToolsContextType {
	mfeVersions: ReturnType<typeof useMfeVersions>['mfeVersions'];
	isVisible: boolean | null;
	hideDevTools: () => void;
	showDevTools: () => void;
	position: DevToolsPosition;
	setPosition: (position: DevToolsPosition) => void;
}

const DevToolsContext = createContext<DevToolsContextType | undefined>(undefined);

export const useDevTools = () => {
	const context = useContext(DevToolsContext);
	if (!context) {
		throw new Error('useDevTools must be used within a DevToolsProvider');
	}
	return context;
};

interface DevToolsProviderProps {
	children: React.ReactNode;
}

export const DevToolsProvider: FC<DevToolsProviderProps> = ({ children }) => {
	const { mfeVersions } = useMfeVersions();
	const { isVisible, hideDevTools, showDevTools, position, setPosition } =
		useDevToolsPreferences();

	const contextValue = useMemo(
		() => ({
			mfeVersions,
			isVisible,
			hideDevTools,
			showDevTools,
			position,
			setPosition
		}),
		[mfeVersions, isVisible, hideDevTools, showDevTools, position, setPosition]
	);

	return <DevToolsContext.Provider value={contextValue}>{children}</DevToolsContext.Provider>;
};

export const withDevTools = <T extends object>(Component: React.ComponentType<T>) => {
	if (!IS_DEVTOOLS_ENABLED) {
		return () => null;
	}

	const DevToolsWrappedComponent = (componentProps: T) => {
		return (
			<DevToolsProvider>
				<Component
					// eslint-disable-next-line react/jsx-props-no-spreading
					{...componentProps}
				/>
			</DevToolsProvider>
		);
	};

	DevToolsWrappedComponent.displayName = `withDevTools(${
		Component.displayName || Component.name
	})`;

	return DevToolsWrappedComponent;
};
